/* The sheet's matrix — the mock's `mxGrid3`, at its tighter cell size.
 *
 * One `46px` label track and one equal track per cycle, `4px` apart. The row label doubles as the
 * set-type menu's trigger, which is why it is a button and not a header cell: the mock puts the set
 * role behind the badge on the left of each row, and the set table two boxes away authors the same
 * thing through the same operation.
 *
 * ── The cell's five readings, from `matrixCellState` ────────────────────────────────────────
 * The ring carries the state and the `<small>` under the number says it in words, so the grid is
 * readable with no colour at all — every cell also carries the state on its `aria-label`. The
 * accent appears on exactly one of them, the pinned cell, which is one of this surface's two accent
 * uses.
 *
 * ── `cellFold`, and the cap the mock does not have ──────────────────────────────────────────
 * The mock staggers each cell in by `ci * 0.03 + ri * 0.02` seconds, folding down from its top edge.
 * The RATIO is the mock's, carried as whole steps on `--fold-step`; the milliseconds are tokens, and
 * so is the ceiling. At the plan's 52-cycle cap the mock's own arithmetic puts the last cell 1,6s
 * behind the first — a grid still arriving long after it has been read — so the delay is clamped to
 * `--dur-cell-stagger-cap`. It is opacity and transform only, and the whole grid's geometry is
 * settled before the first frame.
 *
 * ── It FITS OR SCROLLS ──────────────────────────────────────────────────────────────────────
 * The cycle tracks have a floor rather than being equal shares of whatever room there is: a 30-cycle
 * phase divides the 640px pane into 16px tracks, which clips „26,5 kg" in every cell and every head.
 * `.scroller` is what carries the overflow instead. They have a ceiling too, since the sheet took the
 * whole day modal (owner, 12 October 2026): see `--w-prog-cell-max`.
 */

.scroller {
  /* `flex: none` is load-bearing, not tidiness. This is a flex ITEM of the pane's scroller, so it
   * shrinks by default — and with `overflow-y: hidden` a shrunk box CLIPS. Measured 14 September
   * 2026: with the `?` hints on, the pane's column ran over, this box lost 40px and the grid's last
   * row disappeared, silently, with no scrollbar anywhere to say so. The pane's own scroller is what
   * carries the block axis; this one carries the inline axis and nothing else. */
  flex: none;

  /* The block-start padding used to be the pinned cell's ✕, which hung 5px past the top-right of its
   * box and would have been cut off the whole first row. Since 15 September 2026 that control sits
   * INSIDE the cell (see `.revert`), so nothing overhangs and the allowance is gone; the margin is
   * the grid's own room under the sentence and stays. */
  margin-block-start: var(--space-5px);
  overflow: auto hidden;
}

.grid {
  /* The containing block for the „TU JEST KLIENT" marker, which is an absolutely positioned GRID
   * ITEM: its grid area becomes its containing block, so it tracks the column without being placed
   * in the flow. See `.nowMarker`. */
  position: relative;

  /* ONE track for both variants. A lived cell keeps the regular cell's box (owner, 15 September
   * 2026): its halves print a bare number over the paired column, which is what the template's floor
   * was sized for. A wider live floor scrolled the mock's own five-cycle sheet sideways. */
  display: grid;
  grid-template-columns:
    var(--w-prog-matrix-label)
    repeat(var(--prog-cycles, 5), minmax(var(--w-prog-cell-min), var(--w-prog-cell-max)));
  gap: var(--space-1);

  /* The tracks stop at their ceiling, and a phase too short to fill the full-width pane stands at
   * its start rather than spreading its cells apart. */
  justify-content: start;
  align-items: stretch;
}

/* ONE declaration for both, because they fold as one grid: the row header is the row's first cell.
 * `--fold-step` is that cell's position in the mock's 3:2 ratio and the `min()` is the cap — see the
 * header. It has to be resolved HERE rather than on `.grid`, because a custom property is computed
 * at the element that declares it and inherits already substituted. */
.cell,
.rowLabel {
  animation: cell-fold var(--dur-fold) var(--ease-default) both;
  animation-delay: min(
    calc(var(--fold-step, 0) * var(--dur-cell-stagger-step)),
    var(--dur-cell-stagger-cap)
  );
}

/* The empty corner over the label column. */
.corner {
  min-height: var(--space-7);
}

.cycleHead {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: var(--h-prog-cell);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-8px);
}

.cycleLabel {
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

.cycleTag {
  font-size: var(--fs-badge);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-eyebrow);
  text-transform: uppercase;
  color: var(--text-2);
}

/* ── The row label, which is also the set-type trigger ───────────────────────────────────── */

.rowLabel {
  display: flex;
  flex-direction: column;
  gap: var(--space-1px);
  align-items: center;
  justify-content: center;
  padding: var(--space-0);
  cursor: pointer;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-mark);
}

.rowLabel:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.rowBadge {
  display: grid;
  place-items: center;
  width: var(--space-20px);
  height: var(--space-4);
  font-size: var(--fs-badge);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  background: var(--bg-chip);
  border-radius: var(--radius-5px);
}

/* The mock's `TCOL`: the top set is the inverted one, AMRAP takes the accent wash, the rest stay
 * neutral. The letter is never the only channel — the row's accessible name says the role in full. */
.rowBadge[data-kind='top'] {
  color: var(--bg-card);
  background: var(--text);
}

.rowBadge[data-kind='amrap'] {
  color: var(--accent-ink);
  background: var(--accent-soft);
}

.rowIndex {
  font-size: var(--fs-badge);
  font-weight: var(--fw-semibold);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

/* ── The cells ───────────────────────────────────────────────────────────────────────────── */

.cell {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: var(--h-prog-cell);
  padding: var(--space-3px) var(--space-6px);
  font-variant-numeric: tabular-nums;
  background: var(--bg-card);
  border: var(--border-thick) solid var(--prog-sheet-base);
  border-radius: var(--radius-7px);
}

@keyframes cell-fold {
  from {
    opacity: 0;
    transform: translateY(var(--space-minus-2px)) scaleY(0.92);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

/* HELD at the finished frame, never merely undelayed: a zero duration on a `both`-filled entrance
 * with its delay intact leaves every cell at its `from` keyframe, which is an invisible grid. */
@media (prefers-reduced-motion: reduce) {
  .cell,
  .rowLabel {
    animation: none;
  }
}

.cell[data-state='certain'] {
  background: transparent;
  border-color: var(--prog-sheet-static);
}

.cell[data-state='estimate'] {
  background: var(--prog-sheet-result-wash);
  border-style: dashed;
  border-color: var(--prog-sheet-result);
}

.cell[data-state='derived'] {
  background: transparent;
  border-style: dashed;
  border-color: var(--border-2);
}

.cell[data-state='manual'] {
  background: var(--prog-sheet-manual-wash);
  border-color: var(--prog-sheet-manual);
}

.cell[data-state='flat'] {
  background: transparent;
  border-color: var(--prog-sheet-flat);
}

/* The cell whose field is OPEN — the trainer is typing into this one and no other.
 *
 * It sits after every `[data-state]` rule because it has to WIN over all six at equal specificity,
 * and it is the accent's second appearance on this surface: the pinned cell, the focus ring and this
 * one, which is the same act a moment earlier. Border and ink only — no wash behind the caret, and
 * no glow of any colour anywhere in this app.
 *
 * ── The mock's pulse is NOT ported, and the mock's own source is the reason ─────────────────
 * `Builder planu - modal - progresja.dc.html` puts `cellPulse 1.6s ease-in-out infinite` on its open
 * cell — `@keyframes cellPulse{0%,100%{opacity:1}50%{opacity:.25}}`, which fades the WHOLE box. That
 * is safe there because the mock's open cell contains no field: tapping it opens a popover BESIDE
 * the cell (`pop`, `top: calc(100% + 8px)`) and the number is typed in there. Ours opens the field
 * INSIDE the cell, so the same keyframe would fade the digits and the caret the trainer is watching
 * to 25% twice a second. And the mock applies that very keyframe to its own caret at 1s
 * (`animation: 'cellPulse 1s steps(2,end) infinite'`), so porting both would put two different blink
 * rates on one 33px box. The accent ring is steady instead; the caret is the thing that blinks. */
.cell[data-editing='true'] {
  border-color: var(--accent);
}

/* A cycle the client has already trained. Never drawn on a template, where nothing is consumed.
 *
 * ── NO `opacity` DIM, and it was never doing anything anyway ────────────────────────────────
 * It was `opacity: .62`, and `.cell` carries `animation: cell-fold … both` — a `both`-filled
 * entrance holds its `to` keyframe, which sets `opacity: 1`, and an animation wins over a declared
 * value for the whole life of the element. So the dim was dead for every trainer with motion on, and
 * for the one who asked the operating system to turn motion OFF it came back: the reduced-motion
 * block drops the animation, the declared opacity took over, and `.asked` fell to 62% of `--text-2`.
 * A dim readable only by the people who asked for less motion is a contrast failure in exactly the
 * place it is least acceptable.
 *
 * So the consumed cycle is marked on the GROUND instead, which no entrance holds a frame of and no
 * ink pays for: the sunken surface the split cell already sits on. */
.cell[data-locked='true'] {
  cursor: default;
  background: var(--bg-inset);
}

.cell[data-editable='true'] {
  cursor: cell;
}

.cell:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-minus-2px);
}

.value {
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-load);
  color: var(--text);
}

.cell[data-state='estimate'] .value {
  color: var(--prog-sheet-result-ink);
}

.cell[data-state='manual'] .value {
  color: var(--prog-sheet-manual-ink);
}

.cell[data-state='flat'] .value {
  color: var(--text-2);
}

.annotation {
  overflow: hidden;
  max-width: 100%;
  font-size: var(--fs-badge);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The field a cell opens. It fills the cell so the number does not jump as the box changes mode. */
.editor {
  width: 100%;
  font: inherit;
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
  color: var(--text);
  text-align: center;
  background: transparent;
  border: none;
  outline: none;
}

/* The ink of the OPEN field — the accent's other half on the cell being typed into. It sits here
 * rather than beside the border rule above because the border rule has to win over the six
 * `[data-state]` rules, and this one has to lose to nothing: `.editor` must be declared first. */
.cell[data-editing='true'] .editor {
  color: var(--accent-ink);
}

/* The control that hands a pin back. It stays at `tabIndex: -1` for the pointer — the keyboard
 * reverts with Delete on the cell itself, which is where focus already is and which this button's
 * own accessible name says.
 *
 * ── Quiet by construction, on the owner's call of 15 September 2026 ─────────────────────────
 * It was a filled `--prog-sheet-manual` disc hung 5px outside the cell's top-right corner. On a
 * column of pins that is a row of saturated dots sitting ON the grid rather than in it, and the
 * manual state's own ring and ink already say the cell is pinned — the disc was a second, louder
 * mark for the same fact. So: no fill, no colour, neutral ink, INSIDE the corner, and low emphasis
 * until the pointer or focus is on the cell. The mock draws no badge here at all, so nothing is
 * being departed from; see `SheetMatrix.tsx`'s `CrossGlyph`.
 *
 * `--space-11px` square in the corner of a 109 by 33 cell leaves the centred number untouched. */
.revert {
  position: absolute;
  inset-block-start: var(--space-1px);
  inset-inline-end: var(--space-1px);
  display: grid;
  place-items: center;
  width: var(--space-11px);
  height: var(--space-11px);
  padding: var(--space-0);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: none;
  border-radius: var(--radius-4px);

  /* NOT hidden at rest. A pin is a state the trainer authored, and the only way to take it back with
   * the mouse must be visible on the cell that carries it — a control that appears solely on hover
   * cannot be found by anyone who is not already pointing at it. */
  opacity: 0.55;
}

/* Full emphasis the moment the trainer is anywhere near it — on the CELL, not only on the 11px
 * glyph, because the cell is the target a pointer is already over. The focused state carries the
 * ring in the same rule, so there is one declaration per state rather than two that have to be read
 * together. */
.revert:hover {
  color: var(--text);
  opacity: 1;
}

.revert:focus-visible {
  color: var(--text);
  opacity: 1;
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.cell:hover .revert {
  color: var(--text);
  opacity: 1;
}

.revert svg {
  display: block;
  width: 100%;
  height: 100%;
}

/* ── The type menu's entries ─────────────────────────────────────────────────────────────── */

.typeEntry {
  display: grid;
  grid-template-columns: var(--space-22px) 1fr;
  gap: var(--space-1px) var(--space-2);
  align-items: center;
  min-width: 0;
}

.typeBadge {
  grid-row: 1 / span 2;
  display: grid;
  place-items: center;
  width: var(--space-22px);
  height: var(--space-4);
  font-size: var(--fs-badge);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  background: var(--bg-chip);
  border-radius: var(--radius-5px);
}

.typeBadge[data-kind='top'] {
  color: var(--bg-card);
  background: var(--text);
}

.typeBadge[data-kind='amrap'] {
  color: var(--accent-ink);
  background: var(--accent-soft);
}

.typeNote {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-medium);
  color: var(--text-2);
  white-space: nowrap;
}

/* ── The LIVE variant: where the client is, and what they did with what they were given ─────
 *
 * Drawn only on a fork the client is training. A template gets none of it — `matrixCellState`
 * answers `null` for every one of these, so the selectors below never match and the grid is the one
 * this surface has always drawn.
 */

/* The mock's own inverted head on the open cycle: white box, dark number, the sub-line a step
 * quieter. `--text` / `--bg-card` is the same inversion `.rowBadge[data-kind='top']` uses, so the
 * two inverted chips on this surface are one pair rather than two opinions. */
.cycleHead[data-now='true'] {
  background: var(--text);
  border-color: var(--text);
}

.cycleHead[data-now='true'] .cycleLabel {
  color: var(--bg-card);
}

.cycleHead[data-now='true'] .cycleTag {
  color: var(--bg-card);
  opacity: 0.7;
}

/* „TU JEST KLIENT" — the mock's `nowLineC`, tracking the open cycle's column.
 *
 * ── Absolutely positioned, and that is load-bearing ─────────────────────────────────────────
 * It is a GRID ITEM with an explicit area, so its containing block is that column's track and the
 * outline hugs it at any width — the mock has to write `calc(56px + 8px + col * (NOW − 1) + …)`
 * because its own outline is positioned over the grid rather than inside it. `position: absolute`
 * is what keeps it out of the FLOW: an in-flow item explicitly placed in a row the header cells
 * auto-fill pushes those cells sideways, and the first data row then started one column late.
 *
 * `inset: -5px` is the mock's own bleed (`left: … − 5px; width: calc(col + 10px)`). `z-index` puts
 * it over the cells' borders and `pointer-events: none` keeps every cell under it clickable. */
.nowMarker {
  position: absolute;
  z-index: 2;
  inset: var(--space-minus-5px);
  pointer-events: none;

  /* The mock's own `rgba(255,255,255,.35)`, which is brighter than either border token: this outline
   * has to be read ACROSS five columns of dashed cell rings, and `--border-2` disappeared into them.
   * It took `--text-3` until 15 September 2026 — the tertiary ink this surface uses only as a MARK —
   * and in the LIGHT theme that measured 2.86:1 on `--bg-inset` and 2.37:1 on `--bg-sunken`, under
   * the 3:1 a UI boundary owes (WCAG 1.4.11). `--prog-sheet-now` is the same value in dark and a
   * derived one in light; the token carries both measurements. */
  border: var(--border-thick) solid var(--prog-sheet-now);
  border-radius: var(--radius-12px);
}

/* The tag itself, centred over the outline's top edge — the mock's `nowTagC`, at its own −16px. The
 * room it stands in is the scroller's block-start padding below; without that the tag would be the
 * one thing an `overflow: auto` box clips. */
.nowTag {
  position: absolute;
  inset-block-start: var(--space-minus-14px);
  inset-inline-start: 50%;
  padding: var(--space-1px) var(--space-2);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-eyebrow);
  color: var(--bg-card);
  text-transform: uppercase;
  white-space: nowrap;
  background: var(--text);
  border-radius: var(--radius-5px);
  transform: translateX(-50%);
}

/* AT THE TWO ENDS IT STOPS BEING CENTRED. The tag is wider than a cycle track, so over the first or
 * the last column half of it hangs past the grid — and what hangs off the scroller's START cannot be
 * scrolled back to at all: measured on a 12-cycle fork, the marker on the last column read
 * „TU JEST KLIEN". Which end it is, is `nowColumn.align`'s answer, because the model knows the
 * position and the column count and a stylesheet knows neither. */
.nowTag[data-align='start'] {
  inset-inline-start: var(--space-0);
  transform: none;
}

.nowTag[data-align='end'] {
  inset-inline: auto var(--space-0);
  transform: none;
}

/* The room the tag stands in. It is on the LIVE variant alone, so the template's grid sits exactly
 * where it always has — and it is reserved from the first paint, because the data arrives on the
 * same `/full` read as the plan and there is no second loading state to grow into. */
.scroller[data-live='true'] {
  /* The tag hangs above the column heads and the outline bleeds 5px past the last row, and this box
   * clips the block axis by construction — see `.scroller`'s own note. Room for both is RESERVED
   * rather than grown into: the record arrives on the same `/full` read as the plan, so there is no
   * second state for the grid to settle from. */
  padding-block: var(--space-4) var(--space-5px);
}

/* ── The split cell ────────────────────────────────────────────────────────────────────────
 *
 * OURS, not the mock's: the mock prints one number in a darker box for a logged cycle. The owner's
 * spec of 15 September 2026 is two halves side by side and ONE line between them, ZADANE on the
 * left, WYKONANE on the right, with the two words said once in the column head. It replaced the
 * prescription stacked over the performance, which he rejected as unreadable.
 *
 * Each half stacks the same two type sizes an unlived cell does (its number over its sub-line), and
 * the cell sits on the same track, so a lived cell's box IS an unlived cell's (owner, 15 September
 * 2026, pinned by `plan-progression-live`). Anything longer elides inside its half; the whole row is
 * on the half's title and in the cell's name.
 */

/* SOLID, whatever the rule above it is doing. The dashed hedge belongs to a cell that is guessing,
 * and a session that happened is the one thing on this grid nobody is guessing about. The OUTLINE
 * stays neutral on every verdict: the right half's ink already carries it, and a coloured ring
 * around both halves coloured the prescription with the client's answer. */
.cell[data-lived] {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: stretch;
  padding: var(--space-0);
  background: var(--bg-inset);
  border-style: solid;
  border-color: var(--prog-sheet-flat);
}

.half {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;

  /* 2px inline, not the unlived cell's 6px: a half is half a track, and „102,5 ✓" needs 54px of a
   * 120px cell's half at the mock's four visible cycles (measured 15 September 2026). */
  padding: var(--space-3px) var(--space-2px);
}

/* The one line. Full height, because the two halves are two statements and nothing about either may
 * read as belonging to the other. */
.half + .half {
  border-inline-start: var(--border-hairline) solid var(--border-2);
}

.halfValue,
.halfRest,
.halfWord {
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.halfValue {
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  font-variant-numeric: tabular-nums;
  letter-spacing: var(--ls-load);
  color: var(--text-2);
}

.halfRest,
.halfWord {
  font-size: var(--fs-badge);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

/* The answer carries the emphasis: what the client did is what a trainer opens a lived cycle for. */
.half[data-side='performed'] .halfValue {
  font-weight: var(--fw-bold);
  color: var(--text);
}

/* The colour is the BACKEND's, per line: green on a judged row, red on the line whose column the
 * row's `shortfallFields` names (owner, 15 September 2026). An arrow never decides it. */
.cell[data-lived='delivered'] .half[data-side='performed'] :is(.halfValue, .halfRest),
.cell[data-lived='short'] .half[data-side='performed'] :is(.halfValue, .halfRest) {
  color: var(--prog-sheet-ok-ink);
}

.cell[data-lived] .half[data-side='performed'] [data-short='true'] {
  color: var(--prog-sheet-short-ink);
}

/* ↑ or ↓ after the number it describes, at the sub-line's size on either line, in that line's ink.
 * Never the only channel: the cell's accessible name says „powyżej" or „poniżej". */
.direction {
  display: inline-block;
  width: var(--space-10px);
  height: var(--space-10px);
  margin-inline-start: var(--space-1px);
  vertical-align: baseline;
}

/* The ✗ of a short row no red line shows, beside the large line at the sub-line's size. */
.verdictMark {
  margin-inline-start: var(--space-2px);
  font-size: var(--fs-badge);
  font-weight: var(--fw-bold);
  color: var(--prog-sheet-short);
}


/* A session the client did not do: a red ✗, centred, and nothing else in that half. */
.missedMark {
  font-size: var(--fs-badge);
  color: var(--prog-sheet-short);
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
}

/* ZADANE | WYKONANE under a lived column's number, one word over each half of the cells below.
 * No eyebrow tracking: at the regular track „WYKONANE" measured 68px against a 59px half with it
 * (15 September 2026), and a word cut to „WYKON…" says less than an untracked one. */
.halfHeads {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-self: stretch;
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  text-align: center;
  text-transform: uppercase;
}

/* A head is as narrow as its track, so each word elides in its own half rather than running into the
 * next column. The cells' names say „zadane" and „wykonane" in full. */
.halfHeads > span {
  overflow: hidden;
  min-width: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cycleHead[data-now='true'] .halfHeads {
  color: var(--bg-card);
}
